import React, { useContext, useEffect, useMemo } from 'react';
import styled from 'styled-components/macro';
import { Redirect, Route, Switch, useHistory } from 'react-router';
import moment from 'moment';
import { Link, NavLink } from 'react-router-dom';
import CamsSlider from '../Cams/CamsSlider/CamsSlider';
import { Archive, MetartIntimateUpdates, StaffSelectionUpdates, Stream, Upcoming } from './index';
import { routes, updatesRoutes } from 'src/client/utils/router';
import { Tab, TABLET_LANDSCAPE } from '@tovia/man-ui';
import { useSelector } from 'src/client/redux/modules/helpers/useSelector';
import urls from 'src/shared/urls';
import { Tab as TabType, useTabs } from 'src/client/helpers';
import { camsSliderSettings } from 'src/client/helpers/constants';
import Helmet from '../Helmet/Helmet';
import { StaffSelectionHeader } from './StaffSelections/StaffSelectionHeader';
import { PrivateRoute } from 'src/client/utils/PrivateRoute';
import { UpdatesBanner } from './UpdatesBanner';
import { CamsContext } from '../Cams/CamsContextProvider';
import { UpdatesTabGrid, UpdatesTabs } from './UpdatesTabGrid';
import Loading from '../Loading';
import { CamsTitle } from '../Cams/CamsSection/CamsTitle';
import { MetartIntimateHeader } from './MetartIntimate/MetartIntimateHeader';
import { ArchiveHeader } from './Archive/ArchiveHeader';
import { MainPageTitle } from 'src/client/components/layouts/MainPageTitle';
import { OnPageText } from 'src/client/components/OnPageSeo/OnPageText/OnPageText';

const {
  metartIntimate: intimateRoute,
  stream: streamRoute,
  upcoming: upcomingRoute,
  archive: archiveRoute,
  oldArchive: oldArchiveRoute,
  archiveDate: archiveDateRoute,
  staffSelections: staffSelectionsRoute,
} = routes.updates.routes;

const useUpdateTabs = (): TabType[] => {
  const disableMetartIntimate = useSelector((state) => state.app.config.disableMetartIntimate);
  const hasStaffSelection = useSelector((state) => state.app.config.hasStaffSelection);

  return useMemo(
    () => [
      {
        value: 'archive',
        to: '/updates/archive',
        name: 'Monthly',
        headerTitle: <ArchiveHeader />,
        showCamsOnTab: true,
      },
      {
        value: 'updates',
        to: '/updates',
        name: 'Stream',
        activeClassName: 'route-active',
        headerTitle: 'Updates Stream',
        showCamsOnTab: true,
      },
      {
        value: 'upcoming',
        to: '/updates/upcoming',
        name: 'Upcoming',
        headerTitle: 'Upcoming Releases',
        showCamsOnTab: true,
      },
      ...(!disableMetartIntimate
        ? [
            {
              value: 'intimate',
              to: '/updates/intimate',
              name: 'MetArt Intimate',
              headerTitle: <MetartIntimateHeader />,
              showCamsOnTab: true,
            },
          ]
        : []),
      ...(hasStaffSelection
        ? [
            {
              value: 'staff',
              to: '/updates/staff',
              name: 'Staff Selections',
              requiresAuth: true,
              headerTitle: <StaffSelectionHeader />,
            },
          ]
        : []),
    ],
    [disableMetartIntimate, hasStaffSelection],
  );
};

export default function Updates() {
  const history = useHistory();
  const config = useSelector((state) => state.app.config);
  const { user } = useSelector((state) => state.auth);
  const site = useSelector((state) => state.site);
  const { error, galleriesSinceLastVisit } = useSelector((state) => state.updates);
  const cams = useContext(CamsContext);
  const updateTabs = useUpdateTabs();
  const [tabs, activeTab] = useTabs('updates', Object.values(updateTabs), updatesRoutes);

  useEffect(() => {
    document.body.classList.add('show-jsd'); // show jsd plugin
    return () => document.body.classList.remove('show-jsd');
  }, []);

  useEffect(() => {
    if (error?.status === 404) {
      history.replace(urls.get.notFound);
    }
  }, [error, history]);

  const userLastVisit = user?.lastVisit;
  const lastVisit =
    userLastVisit && !isNaN(+userLastVisit)
      ? moment(+userLastVisit * 1000).format('MMMM, DD YYYY')
      : moment().format('MMMM, DD YYYY');

  const newSubtitle =
    userLastVisit && galleriesSinceLastVisit && config.updates.showGalleriesSinceLastVisit ? (
      <NavLink to="/galleries/latest" className="gallery-notice">
        {`${galleriesSinceLastVisit} New Galleries since your last visit on ${lastVisit}`}
      </NavLink>
    ) : null;

  const showCamsTitle = activeTab?.showCamsOnTab && cams.loaded;

  return (
    <UpdatesWrapper className="content container">
      <Helmet
        defaultTitle="Updates Stream - {{domainName}}"
        id="updates"
        variables={{ domainName: site.domain, siteName: site.name }}
      />

      <UpdatesBanner />
      <UpdatesCamSlider camTrackingPosition="sliderTop" settings={camsSliderSettings} />

      <MainPageTitle>{activeTab?.headerTitle}</MainPageTitle>
      <PageSubtitle>{newSubtitle}</PageSubtitle>

      {/* @SEO_CLEANUP */}
      <OnPageText contentName="text1" />
      <UpdatesTabGrid>
        <UpdatesTabs>
          {tabs.map((tab) => (
            <Tab key={tab.value} as={Link} active={activeTab?.value === tab.value} {...tab} />
          ))}
        </UpdatesTabs>
        {showCamsTitle ? <CamsTitle camTrackingPosition="sideBar" hasAllLiveGirls /> : null}
      </UpdatesTabGrid>
      <React.Suspense fallback={<Loading />}>
        <Switch>
          <Route exact path={streamRoute.path} component={Stream} />
          <Route exact path={upcomingRoute.path} component={Upcoming} />
          <Route exact path={intimateRoute.path} component={MetartIntimateUpdates} />
          <Route exact path={[archiveRoute.path, archiveDateRoute.path]} component={Archive} />
          <PrivateRoute component={StaffSelectionUpdates} exact path={staffSelectionsRoute.path} />
          <Redirect exact from={oldArchiveRoute.path} to={archiveRoute.path} />
          <Redirect to={streamRoute.path} />
        </Switch>
      </React.Suspense>
    </UpdatesWrapper>
  );
}

const UpdatesWrapper = styled.div`
  .ui.menu {
    .item:nth-child(n + 4) {
      @media (max-width: 767px) {
        display: none;
      }
    }
  }

  @media (max-width: 575px) {
    .update-stream-card-wrapper {
      width: 100%;
    }
  }

  .gallery-notice {
    display: block;
    padding-bottom: 10px;
  }
`;

const PageSubtitle = styled.div`
  margin: 10px 0;
`;

const UpdatesCamSlider = styled(CamsSlider)`
  display: block;

  @media ${TABLET_LANDSCAPE} {
    display: none;
  }
`;
